import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  SelectWrapper,
  SelectBase,
  Arrow,
  Options,
  OptionItem,
} from "./styles";
import { useMultipleEditionStore } from "../../../../stores/useMultipleEditionStore";
import { useAutoPrefill } from '../../../../hooks/useAutoPrefill';

const CustomSelectME = ({
  className,
  placeholder,
  variant = "gray",
  id_attribute,
  articleId,
  disabled = false,
}) => {
  const { 
    setMultipleDataObjValue, 
    setMultipleDataObjAproval, 
    attributesDisabled, 
    fetchRules 
  } = useMultipleEditionStore();

  const { applyRules, markAsEdited } = useAutoPrefill();

  // 1. Selector del objeto específico para mostrar el valor actual
  const obj = useMultipleEditionStore((state) =>
    state.multipleEditionData
      .find(a => a.articleId === articleId)
      ?.articleData.find(attr => attr.id_attribute === id_attribute)
  );

  const isCurrentlyTargeted = useMultipleEditionStore((state) => {
    const article = state.multipleEditionData.find(a => a.articleId === articleId);
    if (!article || !state.rules || state.rules.length === 0) return false;

    const myRules = state.rules.filter(r => Number(r.target_attribute_id) === Number(id_attribute));

    return myRules.some(rule => {
      const sourceAttr = article.articleData.find(attr => attr.id_attribute === rule.source_attribute_id);
      const currentValue = sourceAttr?.value;

      if (rule.source_value === null) return !!currentValue;
      return Array.isArray(rule.source_value) && rule.source_value.includes(currentValue);
    });
  });

  const isHardDisabled = useMemo(() => {
    return attributesDisabled?.includes(Number(id_attribute));
  }, [attributesDisabled, id_attribute]);

  const editable = !disabled && !isHardDisabled && !isCurrentlyTargeted;

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef(null);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleToggle = () => {
    if (!editable) return;
    if (!isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width + 10,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option) => {
    if (!editable) return;

    const cleanOption = option.trim();
    setIsOpen(false);
    
    markAsEdited(articleId, id_attribute);
    setMultipleDataObjValue(articleId, id_attribute, cleanOption);
    setMultipleDataObjAproval(articleId, id_attribute, 1, null); 
    
    applyRules(articleId, id_attribute, cleanOption);
  };

  const handleClickOutside = (event) => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <SelectWrapper ref={selectRef} className={className}>
      <SelectBase 
        onClick={handleToggle} 
        variant={variant} 
        editable={editable}
        disabled={!editable} 
      >
        <span>{obj?.value || placeholder}</span>
        <Arrow variant={variant} className="material-symbols-outlined">
          arrow_drop_down
        </Arrow>
      </SelectBase>

      {isOpen && editable && (
        <Options open={isOpen} pos={dropdownPos}>
          {Array.isArray(obj?.option_list) &&
            obj?.option_list.map((option, index) => (
              <OptionItem
                key={`${option}-${index}`}
                onClick={() => handleOptionClick(option)}
                selected={obj?.value === option.trim()}
              >
                {option}
              </OptionItem>
            ))}
        </Options>
      )}
    </SelectWrapper>
  );
};

CustomSelectME.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(["gray", "pink", "gray-light"]),
  id_attribute: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  articleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CustomSelectME;